import { UNIVERSAL_SEARCH_QUICK_LINK_ID } from './constants'

import type { FuseResultMatch } from 'fuse.js'

/**
 * Phase identifier for searchable items.
 * Used to control which items are available in different rollout phases.
 * - P0: Initial launch phase
 * - P1: Second phase rollout
 */
export type SearchableItemPhase = 'P0' | 'P1' | 'P2'

/**
 * Search mode determines which search strategies are used.
 * - FUZZY: Client-side Fuse.js only (for AI opt-out, offline, or fallback)
 * - SEMANTIC: Server-side semantic/embedding search only (for testing)
 * - HYBRID: Both fuzzy and semantic, merged with RRF (default for best results)
 */
export enum UniversalSearchMode {
  FUZZY = 'fuzzy',
  SEMANTIC = 'semantic',
  HYBRID = 'hybrid',
}

/**
 * Configuration for fuzzy search (Fuse.js - client-side).
 */
export interface FuzzySearchConfig {
  /** Threshold for standard fuzzy matching (0-1, lower = stricter). */
  standardThreshold: number
  /** Threshold for strict matching (0-1, lower = stricter). */
  strictThreshold: number
  /** Weight for displayName field in search. */
  displayNameWeight: number
  /** Weight for keywords field in search. */
  keywordsWeight: number
}

/**
 * Configuration for semantic search (sent to API).
 * Maps to SemanticSearchConfig on the server.
 */
export interface SemanticSearchConfig {
  /** Minimum similarity score for standard matches (0-1, higher = stricter). */
  similarityThreshold: number
  /** Higher similarity threshold for strictMatchOnly items. */
  strictSimilarityThreshold: number
  /** Bucket size for grouping similar scores (items within this range use priority as tiebreaker). */
  similarityBucketSize: number
  /** Minimum similarity required for keyword match to boost ranking. */
  keywordBoostThreshold: number
  /** Minimum word length for tokenization in text matching. */
  minimumWordLength: number
  /** Whether to filter out common stop words when matching. Default: true */
  ignoreStopWords: boolean
  /** Additional custom stop words to filter out (merged with predefined). Default: null */
  additionalStopWords: string[] | null
  /** If true, only return results with at least one keyword match. Default: false */
  requireKeywordMatch: boolean
  /** If true, skip internal ranking (let RRF handle it). Default: false for semantic-only, true for hybrid */
  skipRanking: boolean
  /** If true, include displayName matches in matched_keywords list. Default: false */
  includeDisplaynameMatches: boolean
  /** Client-side timeout for semantic API call in milliseconds. Default: 3000 */
  timeoutMs: number
  /** Embedding backend to use for semantic search. */
  embeddingBackend: string
  /** Embedding model to use for semantic search. */
  embeddingModel: string
  /** Maximum number of results to return from the API. */
  maxResults?: number
}

/**
 * Configuration for Reciprocal Rank Fusion (RRF) - used in hybrid mode.
 */
export interface RRFConfig {
  /**
   * Rank weighting constant that controls how steeply scores drop off by rank.
   * - Higher k = more equal weighting across ranks (gentler slope)
   * - Lower k = top ranks get much more weight (steeper slope)
   *
   * Default: 60 (industry standard from Cormack, Clarke, Buettcher 2009 paper).
   * This value is used by Elasticsearch, Pinecone, Weaviate, and most hybrid search systems.
   *
   * ⚠️ CAUTION: Only change with careful consideration and thorough testing.
   * The k=60 default has been empirically validated across diverse datasets.
   */
  k: number
  /** Minimum RRF score threshold for standard items. */
  threshold: number
  /**
   * Penalty multiplier for results that only appear in fuzzy search (not semantic).
   * - 1.0 = no penalty (fuzzy-only results keep full RRF score)
   * - 0.7 = 30% penalty (fuzzy-only results get 70% of their RRF score)
   * - 0.5 = 50% penalty
   *
   * Rationale: Fuzzy-only results are more likely to be false positives
   * (e.g., "time" matching "timezone" for query "all the time").
   *
   */
  fuzzyOnlyPenalty: number
}

/**
 * Configuration for Universal Search.
 * Includes settings for fuzzy search, semantic search, and RRF merging.
 * These can be adjusted via LaunchDarkly or DevTools for testing different search behaviors.
 */
export interface UniversalSearchConfig {
  /** Search mode: FUZZY (client-only), SEMANTIC (API-only), or HYBRID (both with RRF). Default: HYBRID */
  mode: UniversalSearchMode
  /** Fuzzy search configuration (Fuse.js - client-side) */
  fuzzy: FuzzySearchConfig
  /** Semantic search configuration (sent to API) */
  semantic: SemanticSearchConfig
  /** Reciprocal Rank Fusion configuration (hybrid mode) */
  rrf: RRFConfig
  /** Minimum query length to trigger search. Default: 3 */
  minQueryLength: number
  /** Debounce delay in milliseconds. Default: 500 */
  debounceMs: number
  /** Score difference threshold for treating scores as equal (0 = exact comparison). Default: 0 */
  scoreDiffThreshold: number
  /** Maximum number of results to return. Default: 10 */
  maxResults: number
  /** Phases that are enabled for search. Items without a matching phase will be filtered out. */
  enabledPhases: SearchableItemPhase[]
}

// ═══════════════════════════════════════════════════════════════════════════
// SEARCH RESULT CATEGORIES & TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Categories for grouping search results.
 * Used for ranking - lower priority number = higher rank in search results.
 */
export enum SEARCH_RESULT_CATEGORY {
  HIGH_RISK = 'high_risk',
  CARE_OPTIONS = 'care_options',
  TOOLKIT = 'toolkit',
  RESOURCES = 'resources',
  EVENTS = 'events',
  COVERAGE = 'coverage',
  OTHER_BENEFITS = 'other_benefits',
  ACCOUNT = 'account',
  FAQS = 'faqs',
  GENERAL_CNT_SUPPORT = 'general_cnt_support',
}

/**
 * Type identifier for searchable item metadata.
 * Determines how the search result is rendered and what action occurs on interaction.
 */
export enum SEARCH_RESULT_TYPE {
  /** Navigable route - clicking navigates to the page */
  STANDARD = 'standard',
  /** Route with parameters - clicking navigates with query params */
  PARAM_ROUTE = 'paramRoute',
  /** Modal - clicking opens an informational modal */
  MODAL = 'modal',
  /** External URL - opens in browser/new tab */
  EXTERNAL_URL = 'externalUrl',
  /** FAQ item - accordion-style expand/collapse */
  FAQ = 'faq',
}

/**
 * Unique identifiers for each searchable item.
 * Used for type-safe references in eligibility checks, analytics, etc.
 */
export enum SEARCHABLE_ITEM_ID {
  // Triage
  ADULT_TRIAGE = 'adult-triage',
  CHILD_TRIAGE = 'child-triage',
  FORKED_TRIAGE = 'forked-triage',
  ONSITE_THERAPY = 'onsite-therapy',

  // Programs (DPLs)
  COACHING = 'coaching',
  PARENTING_COACHING = 'parenting-coaching',
  COUPLES_THERAPY = 'couples-therapy',
  FAMILY_THERAPY = 'family-therapy',
  MEDICATION_CONSULT = 'medication-consult',
  CONTROLLED_MEDS = 'controlled-meds',
  CHILD_MEDICATION_CONSULT = 'child-medication-consult',
  TEEN_MEDICATION_CONSULT = 'teen-medication-consult',
  RENEW_AUD = 'renew-aud',

  // Complex Care / External
  COMPLEX_CARE = 'complex-care',
  DBT = 'dbt',
  IOP = 'iop',
  PHP = 'php',
  RESIDENTIAL = 'residential',
  INPATIENT = 'inpatient',

  // Toolkits
  TOOLKIT_RELATIONSHIPS = 'toolkit-relationships',
  TOOLKIT_WORK = 'toolkit-work',
  TOOLKIT_FAMILY = 'toolkit-family',
  TOOLKIT_NEURODIVERSITY = 'toolkit-neurodiversity',
  TOOLKIT_HEALTH_CHALLENGES = 'toolkit-health-challenges',
  TOOLKIT_WOMENS_HEALTH = 'toolkit-womens-health',
  TOOLKIT_PARENTING = 'toolkit-parenting',
  TOOLKIT_VALUES = 'toolkit-values',
  TOOLKIT_TRAUMA = 'toolkit-trauma',

  // Care Tab
  SESSIONS = 'sessions',
  PROGRESS = 'progress',

  // Library / Resources
  LIBRARY = 'library',
  EVENTS = 'events',
  MEDITATION = 'meditation',
  MINDFUL_MIX = 'mindful-mix',
  SOUNDSCAPES = 'soundscapes',
  BREATHING_EXERCISES = 'breathing-exercises',
  COURSES = 'courses',

  // Benefits / Wellness
  WELLNESS_CHECK_IN = 'wellness-check-in',
  WORKLIFE_SERVICES = 'worklife-services',
  NICOTINE_QUITLINE = 'nicotine-quitline',
  RESOURCE_COORDINATION = 'resource-coordination',

  // Account
  ACCOUNT = 'account',
  SETTINGS = 'settings',
  SESSION_HISTORY = 'session-history',
  COVERAGE_INFO = 'coverage-info',
  HEALTH_PLAN_PAYMENT = 'health-plan-payment',
  PRIVACY_LEGAL = 'privacy-legal',
  CLINICAL_DATA_SHARING = 'clinical-data-sharing',
  ADD_DEPENDENT = 'add-dependent',
  CHANGE_EMAIL = 'change-email',
  MFA = 'mfa',
  PHONE_NUMBER = 'phone-number',
  LOCATION_TIMEZONE = 'location-timezone',
  DISPLAY_LANGUAGE = 'display-language',
  NOTIFICATIONS = 'notifications',
  TRANSFER_ACCOUNT = 'transfer-account',
  ADD_HEALTH_PLAN = 'add-health-plan',
  ADD_PAYMENT_METHOD = 'add-payment-method',
  LATE_CANCELLATION_PAYMENT = 'late-cancellation-payment',
  FORGOT_PASSWORD = 'forgot-password',

  // External Links
  LYRA_APP = 'lyra-app',

  // CNT Support
  CONTACT_CN = 'contact-cn',

  // FAQs
  FAQS = 'faqs',

  // Contact CN (high risk)
  CONTACT_CN_HIGH_RISK = 'contact-cn-high-risk',
}

/**
 * Base interface for all searchable item metadata.
 */
export interface BaseSearchableItemMetadata {
  /** Unique identifier for the searchable item */
  id: SEARCHABLE_ITEM_ID
  /** Display name shown in search results */
  displayName: string
  /** Keywords for fuzzy search matching */
  keywords: string[]
  /** Category for group-level ranking */
  category: SEARCH_RESULT_CATEGORY
  /** Priority within category (lower = higher rank). Used for tie-breaking. */
  priority?: number
  /**
   * When true, requires a stricter match (lower threshold) to appear in results.
   * Used for sensitive programs where vague searches should not surface the item.
   * Standard fuzzy search uses a higher threshold.
   */
  strictMatchOnly?: boolean
  /**
   * Phase for rollout control. Items are only searchable when their phase is enabled.
   * Defaults to 'P0' if not specified.
   */
  phase?: SearchableItemPhase
}

/**
 * Standard route that maps directly to a route path.
 * Web uses the path directly, mobile maps to screens via URL_PATH_TO_SCREEN.
 */
export interface StandardRouteMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.STANDARD
  /** Route path (e.g., '/secure/index/search-care') */
  route: string
}

/**
 * Route that requires additional parameters (e.g., routes with query params).
 */
export interface ParamRouteMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.PARAM_ROUTE
  /** Base route path */
  route: string
  /** Route parameters to append */
  params: Record<string, string | undefined>
}

/**
 * Modal item - clicking opens an informational modal.
 */
export interface ModalItemMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.MODAL
}

/**
 * External URL that opens in browser/new tab.
 * Used for oncehub links, microsites, etc.
 */
export interface ExternalUrlMetadata extends BaseSearchableItemMetadata {
  type: SEARCH_RESULT_TYPE.EXTERNAL_URL
  url: string
}

/**
 * FAQ item with question and answer for accordion display.
 * Uses string ID (not enum) since FAQs are dynamically generated from customer data.
 */
export interface FAQItemMetadata extends Omit<BaseSearchableItemMetadata, 'id'> {
  /** Dynamic FAQ ID in format 'faq-{slug}' */
  id: string
  type: SEARCH_RESULT_TYPE.FAQ
  /** The answer content (displayName serves as the question) */
  answer: string
}

/**
 * Union of all searchable item types.
 */
export type SearchableItemMetadata =
  | StandardRouteMetadata
  | ParamRouteMetadata
  | ModalItemMetadata
  | ExternalUrlMetadata
  | FAQItemMetadata

/**
 * Type for category priority configuration.
 * Maps each search category to a priority number (lower = higher priority).
 */
export type CategoryPriorityConfig = Record<SEARCH_RESULT_CATEGORY, number>

/**
 * Default category priority for ranking search results.
 * Lower number = higher priority (shown first when scores are equal).
 */
export const DEFAULT_CATEGORY_PRIORITY: CategoryPriorityConfig = {
  [SEARCH_RESULT_CATEGORY.HIGH_RISK]: 1,
  [SEARCH_RESULT_CATEGORY.CARE_OPTIONS]: 2,
  [SEARCH_RESULT_CATEGORY.TOOLKIT]: 3,
  [SEARCH_RESULT_CATEGORY.RESOURCES]: 4,
  [SEARCH_RESULT_CATEGORY.EVENTS]: 5,
  [SEARCH_RESULT_CATEGORY.COVERAGE]: 6,
  [SEARCH_RESULT_CATEGORY.OTHER_BENEFITS]: 7,
  [SEARCH_RESULT_CATEGORY.ACCOUNT]: 8,
  [SEARCH_RESULT_CATEGORY.FAQS]: 9,
  [SEARCH_RESULT_CATEGORY.GENERAL_CNT_SUPPORT]: 10,
}

/**
 * Result from universal search, including the matched item and relevance score.
 */
export interface UniversalSearchResult {
  /** The matched searchable item metadata */
  item: SearchableItemMetadata
  /**
   * Combined/RRF score. Only present in hybrid mode.
   * In fuzzy-only or semantic-only modes, use fuzzyScore or semanticScore instead.
   */
  score?: number
  /** Match information indicating which fields matched (when includeMatches is enabled) */
  matches?: ReadonlyArray<FuseResultMatch>
  /** Fuzzy search score (0 = perfect, 1 = worst). Present when item matched in fuzzy search. */
  fuzzyScore?: number
  /** Semantic similarity score (0-1, higher = better). Present when item matched in semantic search. */
  semanticScore?: number
  /** Keywords that matched in semantic search */
  semanticMatchedKeywords?: string[]
}

// Re-export FuseResultMatch from fuse.js for convenience
export type { FuseResultMatch }

/**
 * Debug info about eligible pages for troubleshooting search issues.
 */
export interface EligibleItemsDebugInfo {
  totalCount: number
  /** Count of strict match items */
  strictCount: number
  /** Count of standard match items */
  standardCount: number
  itemNames: string[]
}

/**
 * Map of item IDs to additional keywords to append to existing keywords.
 * Used to dynamically enhance search keywords from external sources (e.g., API data).
 */
export type AdditionalKeywords = Partial<Record<SEARCHABLE_ITEM_ID, string[]>>

/**
 * Map of item IDs to keywords that should be removed from the item's keyword list.
 * Used by the debug view's keyword fine-tuning UI to test the effect of removing keywords.
 * Uses `string` keys (rather than SEARCHABLE_ITEM_ID) to support dynamic IDs like FAQ items (e.g., `faq-{slug}`).
 */
export type RemovedKeywords = Partial<Record<string, string[]>>

/**
 * Single result item from semantic search API response.
 */
export interface SemanticSearchResultItem {
  /** The matched item (same shape as searchable items sent in request) */
  item: BaseSearchableItemMetadata
  /** Similarity score (0-1, higher = more similar) */
  similarity_score: number
  /** Keywords that matched the query */
  matched_keywords: string[]
  /** Display name words that matched the query */
  matched_displayname_words: string[]
}

/**
 * Simplified semantic result format for RRF merge.
 * Use toSemanticSearchResult() to convert from SemanticSearchResultItem.
 */
export interface SemanticSearchResult {
  /** Item ID (can be SEARCHABLE_ITEM_ID or dynamic ID like FAQ IDs) */
  id: string
  /** Similarity score (0-1, higher = more similar) */
  similarityScore: number
  /** Keywords that matched the query */
  matchedKeywords?: string[]
}

/**
 * Simplified fuzzy result format for RRF merge.
 * Used by useFuzzySearch to return results before final scoring.
 */
export interface FuzzySearchResult {
  /** The matched searchable item metadata */
  item: SearchableItemMetadata
  /** Fuzzy search score (0 = perfect, 1 = worst) */
  fuzzyScore: number
  /** Match information indicating which fields matched */
  matches?: ReadonlyArray<FuseResultMatch>
}

/**
 * Request payload for semantic search API.
 */
export interface SemanticSearchRequest {
  /** Search query string */
  query: string
  /** Metadata containing categories and searchable items */
  metadata: {
    /** Array of category names in priority order */
    SEARCH_RESULT_CATEGORIES: SEARCH_RESULT_CATEGORY[]
    /** Category name to priority number mapping */
    CATEGORY_PRIORITY: CategoryPriorityConfig
    /** Searchable items with their metadata */
    SEARCHABLE_ITEMS: Array<BaseSearchableItemMetadata>
  }
  /** Semantic search configuration (snake_case for API) */
  config: {
    embedding_backend?: string
    embedding_model?: string
    similarity_threshold: number
    strict_similarity_threshold?: number
    max_results?: number
    similarity_bucket_size?: number
    keyword_boost_threshold?: number
    minimum_word_length?: number
    ignore_stop_words?: boolean
    additional_stop_words?: string[] | null
    require_keyword_match?: boolean
    skip_ranking?: boolean
    include_displayname_matches?: boolean
  }
}

/**
 * Response from semantic search API.
 */
export interface SemanticSearchResponse {
  /** Array of matching results */
  results: SemanticSearchResultItem[]
  /** Total number of matches found */
  total_matches: number
  /** The query that was searched (echoed back) */
  query: string
  /** The config that was actually used (with defaults filled in) */
  config_used: {
    embedding_backend: string
    embedding_model: string
    similarity_threshold: number
    strict_similarity_threshold: number
    max_results: number
    region_name: string
    similarity_bucket_size: number
    keyword_boost_threshold: number
    minimum_word_length: number
    ignore_stop_words: boolean
    additional_stop_words: string[] | null
    require_keyword_match: boolean
    skip_ranking: boolean
    include_displayname_matches: boolean
  }
}

export interface UniversalSearchQuickLink {
  id: UNIVERSAL_SEARCH_QUICK_LINK_ID
  text: string
  onPress: () => void
}

/**
 * Risk level classifications from the safeguard API.
 * BENIGN: Non-safety-impacting content
 * LOW/MEDIUM/HIGH: Increasing severity or risk
 * AMBIGUOUS: Unclear or borderline content requiring further review
 */
export enum SafeguardRiskLevel {
  BENIGN = 'BENIGN',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  AMBIGUOUS = 'AMBIGUOUS',
}

/**
 * Type of risk detected by the safeguard API.
 */
export type SafeguardRiskType = 'SELF_HARM' | 'SUICIDE_IDEATION'

/**
 * Confidence classification for risk assessment.
 */
export type SafeguardConfidenceClassification = 'LOW' | 'MEDIUM' | 'HIGH'

/**
 * High risk assessment details from safeguard API.
 */
export interface SafeguardHighRiskAssessment {
  confidenceScore: number
  confidenceClassification: SafeguardConfidenceClassification
}

/**
 * Individual guardrail output from the safeguard API response.
 */
export interface SafeguardGuardrailOutput {
  type: SafeguardRiskType
  triggered: boolean
  highRiskAssessment?: SafeguardHighRiskAssessment
}

/**
 * Individual result from the safeguard engine.
 */
export interface SafeguardEngineResult {
  label: SafeguardRiskType
  risk: SafeguardRiskLevel
  definitions: string[]
}

/**
 * Classifier configuration used by the safeguard API.
 */
export interface SafeguardClassifierConfig {
  type: string
  vector_type?: string
  embedding_model?: string
  similarity_threshold?: number
  enable_cache?: boolean
  region_name?: string
}

/**
 * Response from the safeguard API.
 */
export interface SafeguardResponse {
  response: {
    guardrailOutputs: SafeguardGuardrailOutput[]
  }
  config_used?: {
    classifiers: Array<SafeguardClassifierConfig & { csv_path?: string[] }>
  }
  engine_result?: {
    results: SafeguardEngineResult[]
    classifiers?: Array<{
      type: string
      latency_ms: number
    }>
    metadata?: {
      vector_type?: string
      embedding_model?: string
      similarity_threshold?: number
      region_name?: string
      storage?: string
      vector_used?: boolean
      similarity_score?: number
      matched_phrase?: string
      latency_ms?: number
      engine_latency?: number
    }
  }
}
